/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { useLogout } from '@euler/app/flows/auth/hooks';
import { SettingsNavParams } from '@euler/app/flows/settings/screens';
import { maskedMobilePhone } from '@euler/app/tv/functions/maskedMobilePhone';
import { StatusColors } from '@euler/components/Colors';
import { TableView } from '@euler/components/TableView';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsWeixinAvailable } from '@euler/functions/useIsWeixinAvailable';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useServiceFactory } from '@euler/services/factory';
import { showAlert, withConfirmation } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback } from 'react';
import { ScrollView } from 'react-native';
import { Cell2 as Cell, Section } from '../components/SettingsTable';
import { WeixinBindStatusLabel } from '../components/WeixinBindStatusLabel';
import { useUserProfileManager } from '../functions';
import { useWeixinLoginBinding } from '../functions/useWeixinLoginBinding';

export const AccountSecurityScreen = wrapNavigatorScreen(
  () => {
    const navigation = useNavigation<StackNavigationProp<SettingsNavParams>>();
    const { rpx } = useResponsiveContext();
    const { userService } = useServiceFactory();
    const loading = useAppLoading();
    const logout = useLogout();
    const isWeixinAvailable = useIsWeixinAvailable();
    const { bindStatus, bindAsync, unbindAsync, bindStatusError } =
      useWeixinLoginBinding();
    const { profile, updateAsync } = useUserProfileManager();
    const { showActionSheetWithOptions } = useActionSheet();

    const onChangePasswordPress = useCallback(() => {
      navigation.navigate('_changePassword', {});
    }, [navigation]);

    const onChangePhonePress = useCallback(() => {
      navigation.navigate('_phone', {
        title: '修改手机号码',
        onDone: async params => {
          await loading.showWithAction(async () => {
            await updateAsync({
              mobile: params.mobile,
              ticket: params.ticket,
              verifyCode: params.verifyCode,
            });
            navigation.pop(2);
          });
        },
      });
    }, [loading, navigation, updateAsync]);

    const onUnbindWeixinLogin = usePersistFn(() => {
      showActionSheetWithOptions(
        {
          options: ['取消微信登录绑定', '返回'],
          destructiveButtonIndex: 0,
          cancelButtonIndex: 1,
        },
        async buttonIndex => {
          if (buttonIndex == 1 || buttonIndex == null) {
            return;
          }
          const confirmed = await withConfirmation({
            title: '确认',
            message:
              '您即将取消当前微信登录绑定，取消后，当您需要再次通过微信登录时，您要再次进行手机短信验证和微信授权才能通过微信登录，是否继续?',
          });
          if (confirmed) {
            try {
              await loading.showWithAction(unbindAsync);
            } catch (e) {
              void showAlert('出错了', (e as Error).message);
            }
          }
        },
      );
    });

    const onDeleteMyAccount = usePersistFn(async () => {
      const confirmed = await withConfirmation({
        title: '确认删除账号',
        message:
          '您即将删除您的用户账号，删除账号后您将不能登录，同时您的所有个人数据将被永久删除，是否继续?',
      });
      if (confirmed) {
        await loading.showWithAction(async () => {
          await userService.deleteMyAccount();
          await logout();
        });
      }
    });

    return (
      <ScrollView
        contentContainerStyle={{
          paddingTop: rpx(15),
          paddingBottom: rpx(15),
        }}
      >
        <TableView>
          <Section>
            <Cell
              title="修改密码"
              detailIcon={'disclosure'}
              onPress={onChangePasswordPress}
            />
            <Cell
              title="手机号"
              detail={maskedMobilePhone(profile.user.mobile) || '(未设置)'}
              detailIcon={'disclosure'}
              onPress={onChangePhonePress}
            />
            {isWeixinAvailable && (
              <Cell
                title="微信账号"
                detail={
                  <WeixinBindStatusLabel
                    status={bindStatus}
                    error={bindStatusError}
                  />
                }
                detailIcon={
                  typeof bindStatus === 'boolean' ? 'disclosure' : undefined
                }
                onPress={
                  bindStatus === false
                    ? bindAsync
                    : bindStatus === true
                    ? onUnbindWeixinLogin
                    : undefined
                }
              />
            )}
          </Section>
          <Section>
            <Cell
              title={'删除账号'}
              detailIcon={'disclosure'}
              titleTextStyle={{
                color: StatusColors.Danger,
              }}
              onPress={onDeleteMyAccount}
            />
          </Section>
        </TableView>
      </ScrollView>
    );
  },
  {
    title: '安全设置',
  },
);
